//ICS-205
import React from 'react';
import { useSelector } from 'react-redux';

import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { FormExportButton } from 'components/IAP/FormExportButton';
import { useIcsFormPdfPreview } from 'components/IAP/context/IcsFormPdfPreviewContext';
import Fema205Card from 'components/IAP/femaCards/Fema205Card';
import FormVersioningBanner from 'components/IAP/femaCards/FormVersioningBanner';
import {
  IncidentComs,
  useIncidentComs,
  useCreateIncidentComs,
  useDeleteIncidentComs,
  useUpdateIncidentComs,
} from 'components/IAP/hooks/incidentComsHooks';
import {
  useIncident,
  useIncidentPermissions,
} from 'components/IAP/hooks/incidentHooks';
import { OperationalPeriodScheduleControl } from './OperationalPeriodScheduleControl';
import { useRecordFormVersionSave } from 'components/IAP/hooks/icsFormVersionHooks';
import {
  useIncidentInfo,
  useUpdateIcsInfo,
} from 'components/IAP/hooks/incidentInfoHooks';
import { useIcsFormFamily } from 'components/IAP/hooks/useIcsFormFamily';
import { useICSContext } from 'components/IAP/planningView/ICSContext';
import { useUscgMapContext } from 'components/MapPage/CorporateViewTabs/LayersTab/UscgMapContext';
import { useGetPositionsForIncident } from 'hooks/incident/useRoster';
import { selectGroupGuid } from 'slices/commonSelectors';

import { INCIDENT } from '../../ICSConstants';
import { getIncidentCardTitle } from '../USCGPage';
import { Ics204CardSection } from './AssignmentList/ics204BoxLabels';
import { BoxInfo, PrintedValue } from './icsBoxPrimitives';
import Ics205SignatureBox from './Ics205SignatureBox';
import IncidentComsTable, { EditableComsFields } from './IncidentComsTable';
import {
  ICS205_BOX_LABELS,
  ICS205_CHANNELS_HINT,
  ICS205_INCIDENT_LOCATION_HINT,
  ICS205_INCIDENT_NAME_HINT,
  ICS205_OPERATIONAL_PERIOD_HINT,
  ICS205_SPECIAL_INSTRUCTIONS_HINT,
} from './ics205BoxLabels';
import SaveableField from './SaveableField';

const ICS_205_EXPORT_FORMNAME = 'ICS_205_CG';

type TypeFilter = 'all' | 'Radio' | 'Nonradio';

const TYPE_FILTERS: { label: string; value: TypeFilter }[] = [
  { label: 'All', value: 'all' },
  { label: 'Radio', value: 'Radio' },
  { label: 'Nonradio', value: 'Nonradio' },
];

/**
 * Incident Radio Communications Plan, the ICS 205-CG.
 *
 * Boxes 1-3 are the read-only header; box 4 is the channel table; box 5 is the
 * special instructions; box 6 is the Communications Unit Leader's signature.
 */
const IncidentRadioCommunicationsPlan = () => {
  const { exportAsForm, isExporting } = useIcsFormPdfPreview();
  const groupGuid = useSelector(selectGroupGuid);
  const [typeFilter, setTypeFilter] = React.useState<TypeFilter>('all');

  const { data: incidentPermissions } = useIncidentPermissions();
  const canEditCards = incidentPermissions?.['incident:editCards'] ?? false;
  const formFamily = useIcsFormFamily();
  const { effectivePeriodNumber, isFuture, readOnlyForms } = useICSContext();
  const { isMapVisible } = useUscgMapContext();
  const readOnly = !canEditCards || readOnlyForms;

  const incident = useIncident();
  const { data: communications, isLoading, error } = useIncidentComs(INCIDENT);
  const { data: roster } = useGetPositionsForIncident();
  const { data: incidentInfo } = useIncidentInfo();
  const updateIcsInfo = useUpdateIcsInfo();
  const createComsMutation = useCreateIncidentComs();
  const updateComsMutation = useUpdateIncidentComs();
  const deleteComsMutation = useDeleteIncidentComs();

  const isSaving =
    createComsMutation.isPending ||
    updateComsMutation.isPending ||
    deleteComsMutation.isPending;

  // The 205 is a versioned form, so each of its writes should leave a draft
  // version behind. Recorded after the write lands, so the server snapshots
  // values that are already persisted.
  const recordVersionSave = useRecordFormVersionSave(
    'ICS_205',
    effectivePeriodNumber
  );
  const recordVersion = React.useCallback(() => {
    if (!readOnlyForms) recordVersionSave.mutate();
  }, [readOnlyForms, recordVersionSave]);

  const workGroups =
    roster
      ?.filter((member) => member.is_workgroup && !member.isResourcePosition)
      .map((wg) => ({ id: wg.id, name: wg.name })) ?? [];

  const handleExport = (format: 'pdf' | 'docx' = 'pdf') => {
    void exportAsForm({
      formname: ICS_205_EXPORT_FORMNAME,
      mapSketch: null,
      format,
    });
  };

  const handleCreate = async (fields: EditableComsFields) => {
    await createComsMutation.mutateAsync({
      ...fields,
      type: fields.type ?? 'Radio',
      archived: false,
      group_guid: groupGuid,
      assignment: undefined,
      work_group_id: fields.work_group_id || undefined,
    });
    recordVersion();
  };

  const handleUpdate = async (
    row: IncidentComs,
    fields: EditableComsFields
  ) => {
    const { assignment: _assignment, ...rowWithoutAssignment } = row;
    await updateComsMutation.mutateAsync({
      comsId: row.id,
      data: {
        ...rowWithoutAssignment,
        ...fields,
        type: fields.type ?? row.type ?? 'Radio',
        assignment: undefined,
        work_group_id: fields.work_group_id || undefined,
      },
    });
    recordVersion();
  };

  const handleDelete = async (row: IncidentComs) => {
    await deleteComsMutation.mutateAsync(row.coms_id);
    recordVersion();
  };

  const handleSaveSpecialInstructions = (value: string) => {
    if (!incidentInfo) return;
    updateIcsInfo.mutate(
      { ...incidentInfo, communicationsSpecialInstructions: value },
      { onSuccess: recordVersion }
    );
  };

  const activeCommunications =
    communications?.filter((comm) => !comm.archived) || [];
  const rowsToShow =
    typeFilter === 'all'
      ? activeCommunications
      : activeCommunications.filter(
          (comm) =>
            (comm.type ?? 'Radio').toLowerCase() === typeFilter.toLowerCase()
        );
  const notLoadingNoError = !isLoading && !error;

  const channelFilters = (
    <div className="tw:flex tw:flex-wrap tw:items-center tw:justify-end tw:gap-4">
      <div className="tw:flex tw:items-center tw:gap-2">
        <span className="tw:text-sm tw:text-muted-foreground">View type:</span>
        <ToggleGroup
          aria-label="Channel type"
          value={[typeFilter]}
          onValueChange={(next: unknown[]) => {
            // Base UI clears the value when the pressed item is toggled off.
            // There is no unfiltered-off state, so the current filter stands.
            const value = next[0];
            if (typeof value === 'string') setTypeFilter(value as TypeFilter);
          }}
          variant="outline"
          size="sm"
          spacing={0}
        >
          {TYPE_FILTERS.map((option) => (
            <ToggleGroupItem key={option.value} value={option.value}>
              {option.label}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </div>
    </div>
  );

  const channelTable = (
    <IncidentComsTable
      rows={rowsToShow}
      workGroups={workGroups}
      onCreate={handleCreate}
      onUpdate={handleUpdate}
      onDelete={handleDelete}
      isSaving={isSaving}
      readOnly={readOnly}
    />
  );

  const exportActions = (
    <FormExportButton
      formname={ICS_205_EXPORT_FORMNAME}
      onExport={handleExport}
      label="Preview ICS-205"
      disabled={isExporting}
    />
  );

  const channelsBody = (
    <>
      {error ? (
        <p className="tw:text-sm tw:text-destructive">
          Error loading communication channels: {error.message}
        </p>
      ) : null}
      {notLoadingNoError ? (
        <div className="tw:flex tw:flex-col tw:gap-2">
          {channelFilters}
          {channelTable}
        </div>
      ) : null}
    </>
  );

  // On the FEMA 205 the channel table is box 4 and special instructions box 5,
  // both titled from the descriptor schema.
  if (formFamily === 'fema') {
    return (
      <Fema205Card headerActions={exportActions} channels={channelsBody} />
    );
  }

  const body = (
    <div className="tw:flex tw:flex-col tw:gap-3">
      <FormVersioningBanner baseId="ICS_205" />

      <div className="tw:grid tw:gap-3 tw:md:grid-cols-3">
        <Ics204CardSection
          boxLabel={ICS205_BOX_LABELS.incidentName}
          testId="ics205-section-incident-name"
          className="tw:mt-0 tw:h-full tw:min-w-0"
          headerAction={
            <BoxInfo label="About the incident name">
              {ICS205_INCIDENT_NAME_HINT}
            </BoxInfo>
          }
        >
          <PrintedValue
            value={incident.data?.name ?? ''}
            testId="ics205-field-incident-name"
          />
        </Ics204CardSection>

        <Ics204CardSection
          boxLabel={ICS205_BOX_LABELS.incidentLocation}
          testId="ics205-section-incident-location"
          className="tw:mt-0 tw:h-full tw:min-w-0"
          headerAction={
            <BoxInfo label="About the incident location">
              {ICS205_INCIDENT_LOCATION_HINT}
            </BoxInfo>
          }
        >
          <PrintedValue
            value={
              incidentInfo?.incidentLocation || incident.data?.address || ''
            }
            testId="ics205-field-incident-location"
          />
        </Ics204CardSection>

        <Ics204CardSection
          boxLabel={ICS205_BOX_LABELS.operationalPeriod}
          testId="ics205-section-operational-period"
          className="tw:mt-0 tw:h-full tw:min-w-0"
          headerAction={
            <BoxInfo label="About the operational period">
              {ICS205_OPERATIONAL_PERIOD_HINT}
            </BoxInfo>
          }
        >
          <OperationalPeriodScheduleControl testId="ics205-field-operational-period" />
        </Ics204CardSection>
      </div>

      <Ics204CardSection
        boxLabel={ICS205_BOX_LABELS.channels}
        testId="ics205-section-channels"
        className="tw:mt-0"
        headerAction={
          <BoxInfo label="About the channel list">
            {ICS205_CHANNELS_HINT}
          </BoxInfo>
        }
      >
        {isLoading ? (
          <p className="tw:text-sm tw:text-muted-foreground">
            Loading channels…
          </p>
        ) : (
          channelsBody
        )}
      </Ics204CardSection>

      <Ics204CardSection
        boxLabel={ICS205_BOX_LABELS.specialInstructions}
        testId="ics205-section-special-instructions"
        className="tw:mt-0"
        headerAction={
          <BoxInfo label="About special instructions">
            {ICS205_SPECIAL_INSTRUCTIONS_HINT}
          </BoxInfo>
        }
      >
        <SaveableField
          id="ics205-special-instructions"
          label={ICS205_BOX_LABELS.specialInstructions}
          hideLabel
          multiline
          rows={3}
          placeholder="Enter a description..."
          disabled={readOnly || !incidentInfo}
          value={incidentInfo?.communicationsSpecialInstructions ?? ''}
          onSave={handleSaveSpecialInstructions}
        />
      </Ics204CardSection>

      <Ics205SignatureBox periodNumber={effectivePeriodNumber} />
    </div>
  );

  // Beside the map on a future period there is no room for card chrome, so the
  // boxes stand on the panel itself, as the 202 does.
  if (isMapVisible && isFuture) {
    return <div className="dark content tw:text-foreground mt-2">{body}</div>;
  }

  return (
    <Card
      data-testid="uscg-205-card"
      data-tour="ics205-card"
      data-form-family="uscg"
      className="dark tw:bg-background tw:text-foreground"
    >
      <CardHeader>
        <CardTitle>
          {getIncidentCardTitle('incidentRadioCommunicationsPlan')}
        </CardTitle>
        <CardAction>{exportActions}</CardAction>
      </CardHeader>
      <CardContent>{body}</CardContent>
    </Card>
  );
};

export default IncidentRadioCommunicationsPlan;
